import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import cn from 'classnames';
import BannerReaderStatus from 'slickpay/online/BannerReaderStatus';
import TipPosBanner from 'slickpay/online/TipPosBanner.component';

import { feBusinessStripeFoldToggle } from 'actions/ui.actions';
import Button from 'components/form/Button.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import LINK from 'constants/link.const';
import STRIPE_STATUS from 'constants/pos/stripeStatus.const';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectCurrentSalonId, selectCurrentSalonRestrictedToCriticalServices, selectCurrentSalonStripeStatus } from 'selectors/currentSalon.selectors';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';

import CN from './StatusBannerOnline.module.scss';

const CTAG = 'StatusBannerOnline';

const L = logger(CTAG);

const BannerStripeStatus = ({ status, isRestrictedToCriticalServices, feBusinessStripeFoldToggle }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const salonId = useCurrentSalonId();
  const nav = navFor('StatusBannerOnline.handleGoToSetupStripe()', { history });

  const isPending = status === STRIPE_STATUS.pending;
  const isEnabled = status === STRIPE_STATUS.enabled;
  const isComplete = status === STRIPE_STATUS.complete;
  const isRestricted = status === STRIPE_STATUS.restricted;
  const isIncomplete = status === STRIPE_STATUS.incomplete;
  const noAccount = status === STRIPE_STATUS.noAccount;

  const isCompleteAndNotRestricted = isComplete && !isRestrictedToCriticalServices;
  const isCompleteAndRestricted = isComplete && isRestrictedToCriticalServices;

  const handleGoToBusinessStripeFold = useCallback(() => {
    feBusinessStripeFoldToggle();
    nav.push({
      pathname: linkTo({ pattern: LINK.salonOnline, params: { salonId } }),
    });
  }, [feBusinessStripeFoldToggle, nav, salonId]);

  L.debug('BannerStripeStatus()', 'status:', { status, isRestrictedToCriticalServices });

  if (noAccount) {
    return (
      <div
        data-bem="BannerStripeStatus__isCompleteAndNotRestricted"
        className={cn({
          [CN.container]: true,
          [CN.restricted]: true,
        })}
      >
        <NavLink to={linkTo({ pattern: LINK.salonOnline, params: { salonId }, fault: '#' })}>
          <div className={CN.text}>
            <span className={CN.statusTitle}>{t('bannerStripeStatus.title')}</span>
            <div className={CN.badgeContainer}>
              <Badge variant="alert">
                <div className={CN.buttonInner}>
                  <Icon variant="xCircleSolid" width="md" />
                  <span className={CN.withIcon}>{t('bannerStripeStatus.badges.noAccount')}</span>
                </div>
              </Badge>
            </div>

            <span className={CN.description}>{t('bannerStripeStatus.descriptions.noAccount')}</span>

            <div className={CN.buttonContainer}>
              <Button variant="primary" onClick={handleGoToBusinessStripeFold} xSpace noMargin>
                {t('bannerStripeStatus.buttons.setupAccount')}
              </Button>
            </div>
          </div>
        </NavLink>
      </div>
    );
  }
  if (isPending || isEnabled) {
    return (
      <div
        data-bem="BannerStripeStatus__pending"
        className={cn({
          [CN.container]: true,
          [CN.pending]: true,
        })}
      >
        <NavLink to={linkTo({ pattern: LINK.salonOnline, params: { salonId }, fault: '#' })}>
          <div className={CN.text}>
            <span className={CN.statusTitle}>{t('bannerStripeStatus.title')}</span>
            <div className={CN.badgeContainer}>
              <Badge variant="attention">
                <div className={CN.buttonInner}>
                  <Icon variant="clockCircleSolid" width="md" />
                  <span className={CN.withIcon}>{t('bannerStripeStatus.badges.pending')}</span>
                </div>
              </Badge>
            </div>

            <span className={CN.description}>
              <span className={CN.purple}> {t('bannerStripeStatus.actions.login')}</span>
              {t('bannerStripeStatus.descriptions.pending')}
            </span>
          </div>
        </NavLink>
      </div>
    );
  }

  if (isCompleteAndNotRestricted) {
    return (
      <div
        data-bem="BannerStripeStatus__isCompleteAndNotRestricted"
        className={cn({
          [CN.container]: true,
          [CN.complete]: true,
        })}
      >
        <NavLink to={linkTo({ pattern: LINK.salonOnline, params: { salonId }, fault: '#' })}>
          <div className={CN.text}>
            <span className={CN.statusTitle}>{t('bannerStripeStatus.title')}</span>
            <div className={CN.badgeContainer}>
              <Badge variant="success" invertedDark>
                <div className={CN.buttonInner}>
                  <Icon variant="checkCircleSolid" width="md" />
                  <span className={CN.withIcon}>{t('bannerStripeStatus.badges.complete')}</span>
                </div>
              </Badge>
            </div>

            <span className={CN.description}>
              <span className={CN.purple}> {t('bannerStripeStatus.actions.login')}</span>
              {S.space}
              {t('bannerStripeStatus.descriptions.complete')}
            </span>
          </div>
        </NavLink>
      </div>
    );
  }

  if (isCompleteAndRestricted) {
    return (
      <div
        data-bem="BannerStripeStatus__isCompleteAndRestricted"
        className={cn({
          [CN.container]: true,
          [CN.restricted]: true,
        })}
      >
        <NavLink to={linkTo({ pattern: LINK.salonOnline, params: { salonId }, fault: '#' })}>
          <div className={CN.text}>
            <span className={CN.statusTitle}>{t('bannerStripeStatus.title')}</span>
            <div className={CN.badgeContainer}>
              <Badge variant="alert">
                <div className={CN.buttonInner}>
                  <Icon variant="checkCircleSolid" width="md" />
                  <span className={CN.withIcon}>{t('bannerStripeStatus.badges.unverified')}</span>
                </div>
              </Badge>
            </div>

            <span className={CN.description}>{t('bannerStripeStatus.descriptions.unverified')}</span>
          </div>
        </NavLink>
      </div>
    );
  }

  if (isRestricted) {
    return (
      <div
        data-bem="BannerStripeStatus__restricted"
        className={cn({
          [CN.container]: true,
          [CN.restricted]: true,
        })}
      >
        <NavLink to={linkTo({ pattern: LINK.salonOnline, params: { salonId }, fault: '#' })}>
          <div className={CN.text}>
            <span className={CN.statusTitle}>{t('bannerStripeStatus.title')}</span>
            <div className={CN.badgeContainer}>
              <Badge variant="restricted">
                <div className={CN.buttonInner}>
                  <span className={CN.emoji}>
                    {S.restricted}
                    {S.space}
                  </span>
                  <span className={CN.withIcon}>{t('bannerStripeStatus.badges.restricted')}</span>
                </div>
              </Badge>
            </div>

            <span className={CN.description}>
              <span className={CN.purple}> {t('bannerStripeStatus.actions.login')}</span>
              {t('bannerStripeStatus.descriptions.restricted')}
            </span>
          </div>
        </NavLink>
      </div>
    );
  }

  if (isIncomplete) {
    return (
      <div
        data-bem="BannerStripeStatus__incomplete"
        className={cn({
          [CN.container]: true,
          [CN.incomplete]: true,
        })}
      >
        <div className={CN.text}>
          <span className={CN.statusTitle}>{t('bannerStripeStatus.title')}</span>
          <div className={CN.badgeContainer}>
            <Badge variant="attention">
              <div className={CN.buttonInner}>
                <Icon variant="off" width="md" />
                <span className={CN.withIcon}>{t('bannerStripeStatus.badges.incomplete')}</span>
              </div>
            </Badge>
          </div>

          <span className={CN.description}> {t('bannerStripeStatus.descriptions.incomplete')}</span>
          <div className={CN.buttonContainer}>
            <Button variant="primary" onClick={handleGoToBusinessStripeFold} xSpace noMargin>
              {t('bannerStripeStatus.buttons.completeSetup')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <div />;
};

const StatusBannerOnline = ({ salonId, status, isRestrictedToCriticalServices, feBusinessStripeFoldToggle }) => (
  <div data-bem="StatusBannerOnline" className={CN.layout}>
    <BannerReaderStatus />

    <BannerStripeStatus status={status} salonId={salonId} isRestrictedToCriticalServices={isRestrictedToCriticalServices} feBusinessStripeFoldToggle={feBusinessStripeFoldToggle} />
    <TipPosBanner />
  </div>
);

const StatusBannerOnlineHoc = connect(
  (state) => ({
    salonId: selectCurrentSalonId(state),
    status: selectCurrentSalonStripeStatus(state),
    isRestrictedToCriticalServices: selectCurrentSalonRestrictedToCriticalServices(state),
  }),

  mapActionsToDispatchToProps({
    feBusinessStripeFoldToggle,
  }),
)(StatusBannerOnline);

export default StatusBannerOnlineHoc;
